import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, CircleIcon, ShieldCheckIcon, LockIcon, ArrowLeftIcon } from 'lucide-react';
interface OrderBump {
  id: string;
  title: string;
  bannerTitle: string;
  subtitle: string;
  description: string;
  price: number;
  imageUrl: string;
  selected: boolean;
  features: string[];
}
export function CheckoutPage() {
  const [orderBumps, setOrderBumps] = useState<OrderBump[]>([{
    id: 'primer-cliente',
    title: 'El Kit del Primer Cliente',
    bannerTitle: 'Kit del Primer Cliente',
    subtitle: '5 Guiones Copiar y Pegar por Nicho',
    description: 'Consigue tu primer cliente hoy mismo con guiones probados',
    price: 17,
    imageUrl: 'https://cdn.magicpatterns.com/uploads/9w5AKdDEzrtDYYLECAUa8H/order_bump_1.png',
    selected: false,
    features: ['Guión para Restaurantes', 'Guión para Dentistas', 'Guión para Talleres', 'Guión para Peluquerías', 'Guión para Tiendas']
  }, {
    id: 'ingresos-recurrentes',
    title: 'La Máquina de Ingresos Recurrentes',
    bannerTitle: 'Ingresos Recurrentes',
    subtitle: 'Convierte cada cliente en $99/mes',
    description: 'Ingresos pasivos predecibles todos los meses',
    price: 37,
    imageUrl: 'https://cdn.magicpatterns.com/uploads/uux9Eddt19Dhrsui7JFMKY/order_bump_2.png',
    selected: false,
    features: ['Sistema de ingresos mensuales', 'Plantillas de propuestas recurrentes', 'Scripts de venta de mantenimiento', 'Calculadora de ingresos pasivos', 'Estrategia de retención de clientes']
  }]);
  const basePrice = 47;
  const selectedBumpsTotal = orderBumps.filter(b => b.selected).reduce((sum, b) => sum + b.price, 0);
  const totalPrice = basePrice + selectedBumpsTotal;
  const toggleBump = (id: string) => {
    setOrderBumps(prev => prev.map(bump => bump.id === id ? {
      ...bump,
      selected: !bump.selected
    } : bump));
  };
  const getCheckoutUrl = () => {
    const bump1Selected = orderBumps.find(b => b.id === 'primer-cliente')?.selected;
    const bump2Selected = orderBumps.find(b => b.id === 'ingresos-recurrentes')?.selected;
    if (bump1Selected && bump2Selected) {
      // AI Web Closer + El Kit del Primer Cliente + Ingresos Recurrentes
      return 'https://offer.aiwebcloser.com/cart/48181021901048:1';
    }
    if (bump1Selected) {
      // AI Web Closer + El Kit del Primer Cliente
      return 'https://offer.aiwebcloser.com/cart/48181020066040:1';
    }
    if (bump2Selected) {
      // AI Web Closer + Ingresos Recurrentes
      return 'https://offer.aiwebcloser.com/cart/48181023310072:1';
    }
    // AI Web Closer only
    return 'https://offer.aiwebcloser.com/cart/48181018525944:1';
  };
  return <div className="min-h-screen bg-navy font-body text-white" data-id="element-774">
      {/* Header */}
      <header className="border-b border-gold/20 bg-navy-dark" data-id="element-775">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-776">
          <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-id="element-777">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-778" />
            <span className="font-body text-sm" data-id="element-779">Volver</span>
          </a>
          <div className="flex items-center gap-2" data-id="element-780">
            <h1 className="font-heading font-black text-xl md:text-2xl" data-id="element-781">
              <span className="text-gold" data-id="element-782">AI</span> Web Closer
            </h1>
          </div>
          <div className="flex items-center gap-2 text-green-500" data-id="element-783">
            <ShieldCheckIcon className="h-5 w-5" data-id="element-784" />
            <span className="font-body text-sm hidden md:inline" data-id="element-785">
              Pago Seguro
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8" data-id="element-786">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-787">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-788">
            {/* Progress Indicator */}
            <div className="flex items-center gap-2 mb-6" data-id="element-789">
              <div className="flex items-center gap-2" data-id="element-790">
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center" data-id="element-791">
                  <span className="font-heading font-bold text-navy text-sm" data-id="element-792">
                    1
                  </span>
                </div>
                <span className="font-body text-sm text-white" data-id="element-793">
                  Revisa tu orden
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-gold/20" data-id="element-794" />
              <div className="flex items-center gap-2" data-id="element-795">
                <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center" data-id="element-796">
                  <span className="font-heading font-bold text-gray-500 text-sm" data-id="element-797">
                    2
                  </span>
                </div>
                <span className="font-body text-sm text-gray-500" data-id="element-798">Pago</span>
              </div>
            </div>

            {/* Main Product */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-gradient-to-br from-navy-dark to-navy border-2 border-gold/30 rounded-xl p-3 md:p-6" data-id="element-799">
              <div className="flex items-start gap-2 md:gap-4 mb-2 md:mb-3" data-id="element-800">
                <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-gold rounded-lg flex items-center justify-center" data-id="element-801">
                  <CheckCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-navy" data-id="element-802" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-803">
                  <h2 className="font-heading font-bold text-base md:text-2xl mb-0.5 md:mb-1" data-id="element-804">
                    AI Web Closer System
                  </h2>
                  <p className="font-body text-gray-300 text-xs md:text-sm" data-id="element-805">
                    Acceso completo al curso + cerebros de IA + guiones +
                    calculadora
                  </p>
                </div>
                <div className="text-right flex-shrink-0" data-id="element-806">
                  <p className="font-heading font-black text-xl md:text-3xl text-gold" data-id="element-807">
                    ${basePrice}
                  </p>
                  <p className="font-body text-[10px] md:text-xs text-gray-400" data-id="element-808">
                    pago único
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 pt-2 md:pt-3 border-t border-gold/10" data-id="element-809">
                <div className="text-center" data-id="element-810">
                  <p className="font-heading font-bold text-gold text-sm md:text-base" data-id="element-811">
                    4
                  </p>
                  <p className="font-body text-[9px] md:text-xs text-gray-400" data-id="element-812">
                    Módulos
                  </p>
                </div>
                <div className="text-center" data-id="element-813">
                  <p className="font-heading font-bold text-gold text-sm md:text-base" data-id="element-814">
                    2
                  </p>
                  <p className="font-body text-[9px] md:text-xs text-gray-400" data-id="element-815">
                    Cerebros IA
                  </p>
                </div>
                <div className="text-center" data-id="element-816">
                  <p className="font-heading font-bold text-gold text-sm md:text-base" data-id="element-817">
                    5
                  </p>
                  <p className="font-body text-[9px] md:text-xs text-gray-400" data-id="element-818">
                    Guiones
                  </p>
                </div>
                <div className="text-center" data-id="element-819">
                  <p className="font-heading font-bold text-gold text-sm md:text-base" data-id="element-820">
                    ∞
                  </p>
                  <p className="font-body text-[9px] md:text-xs text-gray-400" data-id="element-821">
                    Acceso
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Order Bumps Section */}
            <div data-id="element-822">
              <div className="mb-2 md:mb-3" data-id="element-823">
                <h3 className="font-heading font-bold text-base md:text-xl text-gold mb-1" data-id="element-824">
                  ⚡ Acelera Tus Resultados - Altamente Recomendado
                </h3>
                <p className="font-body text-xs text-gray-400" data-id="element-825">
                  Haz clic para añadir a tu orden
                </p>
              </div>

              <div className="space-y-2 md:space-y-3" data-id="element-826">
                {orderBumps.map((bump, index) => <motion.div key={bump.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + index * 0.1
              }} onClick={() => toggleBump(bump.id)} className={`
                      relative cursor-pointer rounded-xl overflow-hidden transition-all duration-300
                      ${bump.selected ? 'ring-2 ring-gold shadow-2xl shadow-gold/20' : 'ring-1 ring-gold/30 hover:ring-gold/50'}
                    `} data-id="element-827">
                    {/* Gold Banner */}
                    <div className="bg-gold px-4 py-2.5 flex items-center justify-between" data-id="element-828">
                      <span className="font-heading font-black text-navy text-sm md:text-base uppercase tracking-wide" data-id="element-829">
                        ⚡ {bump.bannerTitle}
                      </span>
                      <span className="font-heading font-bold text-navy text-xs bg-navy/15 px-2 py-0.5 rounded" data-id="element-830">
                        +${bump.price}
                      </span>
                    </div>

                    <div className="bg-gradient-to-br from-navy-dark to-navy p-4" data-id="element-831">
                      <div className="flex items-start gap-3" data-id="element-832">
                        {/* Checkbox */}
                        <motion.div animate={{
                      scale: bump.selected ? 1.1 : 1
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-833">
                          {bump.selected ? <div className="relative w-6 h-6" data-id="element-834">
                              <div className="absolute inset-0 bg-gold rounded-full" data-id="element-835" />
                              <CheckCircleIcon className="relative h-6 w-6 text-navy" strokeWidth={3} data-id="element-836" />
                            </div> : <CircleIcon className="h-6 w-6 text-gold/60" data-id="element-837" />}
                        </motion.div>

                        {/* Content */}
                        <div className="flex-1" data-id="element-838">
                          <div className="flex items-start justify-between gap-3 mb-2" data-id="element-839">
                            <div data-id="element-840">
                              <h4 className="font-heading font-bold text-lg md:text-xl mb-0.5" data-id="element-841">
                                {bump.title}
                              </h4>
                              <p className="font-body text-xs text-gold mb-1" data-id="element-842">
                                {bump.subtitle}
                              </p>
                              <p className="font-body text-xs text-gray-400" data-id="element-843">
                                {bump.description}
                              </p>
                            </div>
                            <div className="flex-shrink-0 text-right" data-id="element-844">
                              <div className="bg-gold text-navy px-3 py-1.5 rounded-lg" data-id="element-845">
                                <p className="font-heading font-black text-xl" data-id="element-846">
                                  ${bump.price}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Features */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-id="element-847">
                            {bump.features.map((feature, idx) => <div key={idx} className="flex items-center gap-1.5" data-id="element-848">
                                <CheckCircleIcon className="h-3.5 w-3.5 text-gold flex-shrink-0" data-id="element-849" />
                                <span className="font-body text-[11px] text-gray-300" data-id="element-850">
                                  {feature}
                                </span>
                              </div>)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    {bump.selected && <motion.div initial={{
                  scaleX: 0
                }} animate={{
                  scaleX: 1
                }} className="h-1 bg-gradient-to-r from-gold via-gold-light to-gold" data-id="element-851" />}
                  </motion.div>)}
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary (Sticky) */}
          <div className="lg:col-span-1" data-id="element-852">
            <div className="sticky top-4" data-id="element-853">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} className="bg-gradient-to-br from-navy-dark to-navy border border-gold/30 rounded-xl p-6 shadow-2xl" data-id="element-854">
                <h3 className="font-heading font-bold text-xl mb-4 pb-4 border-b border-gold/20" data-id="element-855">
                  Resumen de Orden
                </h3>

                {/* Line Items */}
                <div className="space-y-3 mb-4" data-id="element-856">
                  <div className="flex items-center justify-between" data-id="element-857">
                    <span className="font-body text-sm text-gray-300" data-id="element-858">
                      AI Web Closer System
                    </span>
                    <span className="font-heading font-bold text-white" data-id="element-859">
                      ${basePrice}
                    </span>
                  </div>

                  {orderBumps.filter(b => b.selected).map(bump => <div key={bump.id} className="flex items-center justify-between" data-id="element-860">
                        <span className="font-body text-sm text-gray-300" data-id="element-861">
                          {bump.title}
                        </span>
                        <span className="font-heading font-bold text-gold" data-id="element-862">
                          +${bump.price}
                        </span>
                      </div>)}
                </div>

                {/* Total */}
                <div className="pt-4 border-t border-gold/20 mb-6" data-id="element-863">
                  <div className="flex items-center justify-between mb-2" data-id="element-864">
                    <span className="font-heading font-bold text-lg" data-id="element-865">
                      Total Hoy
                    </span>
                    <span className="font-heading font-black text-3xl text-gold" data-id="element-866">
                      ${totalPrice}
                    </span>
                  </div>
                  <p className="font-body text-xs text-gray-400 text-center" data-id="element-867">
                    Pago único • Acceso inmediato
                  </p>
                </div>

                {/* CTA Button */}
                <motion.a href={getCheckoutUrl()} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full block text-center bg-gold hover:bg-gold-light text-navy font-heading font-bold text-lg px-6 py-4 rounded-xl shadow-2xl transition-all duration-300 mb-4" data-id="element-868">
                  COMPLETAR COMPRA POR ${totalPrice}
                </motion.a>

                {/* Trust Badges */}
                <div className="space-y-2 pt-4 border-t border-gold/10" data-id="element-869">
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-870">
                    <LockIcon className="h-4 w-4 text-green-500" data-id="element-871" />
                    <span className="font-body text-xs" data-id="element-872">
                      Pago 100% seguro y encriptado
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-873">
                    <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-874" />
                    <span className="font-body text-xs" data-id="element-875">
                      Garantía de 30 días
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-876">
                    <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-877" />
                    <span className="font-body text-xs" data-id="element-878">
                      Acceso inmediato después del pago
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Money Back Guarantee */}
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.3
            }} className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center" data-id="element-879">
                <ShieldCheckIcon className="h-8 w-8 text-green-500 mx-auto mb-2" data-id="element-880" />
                <p className="font-heading font-bold text-sm text-green-500 mb-1" data-id="element-881">
                  Garantía de 30 Días
                </p>
                <p className="font-body text-xs text-gray-400" data-id="element-882">
                  Si no consigues ningún cliente interesado, te devolvemos tu
                  dinero
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}